import type { SessionWorkItem, SessionWorkItemKind } from '../types/work-management.js';
import type {
  SessionGoalOption,
  SessionGroup,
  SessionGrouping,
  SessionListCatalogs,
  SessionListFlatRow,
  SessionListQuery,
  SessionListSort,
  SessionListView,
  SessionRef,
} from '../types/session-list.js';
import {
  ALL_FILTER,
  CONVERSATIONS_GROUP_KEY,
  CONVERSATIONS_GROUP_LABEL,
  NO_WORKSPACE_GROUP_KEY,
  NO_WORKSPACE_GROUP_LABEL,
  UNLINKED_GROUP_KEY,
  UNLINKED_GROUP_LABEL,
  WORK_GROUP_KEY,
  WORK_GROUP_LABEL,
} from '../types/session-list.js';

export interface GroupableSession {
  id: string;
  name: string;
  persona_name?: string | null;
  workspace_id?: string | null;
  created_at?: string | null;
  last_accessed?: string;
}

const RUNNING_STATES: Record<SessionWorkItemKind, readonly string[]> = {
  task: ['in_progress'],
  project: ['active'],
  goal: ['decomposing', 'executing', 'verifying'],
};

export function isSessionRunning(items: readonly SessionWorkItem[] | undefined): boolean {
  if (!items) return false;
  return items.some((item) => RUNNING_STATES[item.work_item_kind].includes(item.work_item_state));
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function primaryWorkItem(
  session: GroupableSession,
  workItemsBySession: ReadonlyMap<string, readonly SessionWorkItem[]>,
): SessionWorkItem | null {
  const items = workItemsBySession.get(session.id);
  return items && items.length > 0 ? items[0] : null;
}

function sessionRoleOptions<S extends GroupableSession>(
  sessions: readonly S[],
  workItemsBySession: ReadonlyMap<string, readonly SessionWorkItem[]>,
): string[] {
  const roles = new Set<string>();
  for (const session of sessions) {
    if (!primaryWorkItem(session, workItemsBySession)) continue;
    if (session.persona_name) roles.add(session.persona_name);
  }
  return Array.from(roles).sort();
}

function sessionGoalOptions<S extends GroupableSession>(
  sessions: readonly S[],
  workItemsBySession: ReadonlyMap<string, readonly SessionWorkItem[]>,
): SessionGoalOption[] {
  const goals = new Map<string, string>();
  for (const session of sessions) {
    const item = primaryWorkItem(session, workItemsBySession);
    if (!item?.goal_id) continue;
    goals.set(item.goal_id, item.goal_objective ?? `Goal ${shortId(item.goal_id)}`);
  }
  return Array.from(goals, ([id, label]) => ({ id, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function countGroupedSessions<S>(groups: readonly SessionGroup<S>[]): number {
  return groups.reduce(
    (total, group) =>
      total +
      group.sessions.length +
      group.subgroups.reduce((subtotal, sub) => subtotal + sub.sessions.length, 0),
    0,
  );
}

function countGroupSessions<S>(group: SessionGroup<S>): number {
  return (
    group.sessions.length +
    group.subgroups.reduce((subtotal, sub) => subtotal + sub.sessions.length, 0)
  );
}

interface Level {
  key: string;
  label: string;
  subKey: string | null;
  subLabel: string;
}

function workspaceLevel<S extends GroupableSession>(
  session: S,
  workspaceNames?: ReadonlyMap<string, string>,
): Level {
  const workspaceId = session.workspace_id ?? null;
  return {
    key: workspaceId ?? NO_WORKSPACE_GROUP_KEY,
    label: workspaceId
      ? (workspaceNames?.get(workspaceId) ?? `Workspace ${shortId(workspaceId)}`)
      : NO_WORKSPACE_GROUP_LABEL,
    subKey: null,
    subLabel: '',
  };
}

function levelsFor<S extends GroupableSession>(
  session: S,
  item: SessionWorkItem,
  grouping: SessionGrouping,
  teamNames?: ReadonlyMap<string, string>,
  workspaceNames?: ReadonlyMap<string, string>,
): Level {
  switch (grouping) {
    case 'goal':
      return {
        key: item.goal_id ?? UNLINKED_GROUP_KEY,
        label: item.goal_id
          ? (item.goal_objective ?? `Goal ${shortId(item.goal_id)}`)
          : UNLINKED_GROUP_LABEL,
        subKey: item.project_id ?? null,
        subLabel: item.project_id
          ? (item.project_name ?? `Project ${shortId(item.project_id)}`)
          : '',
      };
    case 'team':
      return {
        key: item.team_id,
        label: teamNames?.get(item.team_id) ?? `Team ${shortId(item.team_id)}`,
        subKey: item.goal_id ?? null,
        subLabel: item.goal_id ? (item.goal_objective ?? `Goal ${shortId(item.goal_id)}`) : '',
      };
    case 'workspace':
      return workspaceLevel(session, workspaceNames);
    case 'user-initiated':
      return { key: WORK_GROUP_KEY, label: WORK_GROUP_LABEL, subKey: null, subLabel: '' };
  }
}

function sortSessions<S extends GroupableSession>(sessions: readonly S[], sort: SessionListSort): S[] {
  if (sort === 'last_activity') return [...sessions];
  const copy = [...sessions];
  if (sort === 'created') {
    copy.sort((a, b) => {
      const aTime = a.created_at ? Date.parse(a.created_at) : 0;
      const bTime = b.created_at ? Date.parse(b.created_at) : 0;
      return bTime - aTime || a.id.localeCompare(b.id);
    });
    return copy;
  }
  copy.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  return copy;
}

function workItemsMap(
  workItems: readonly SessionWorkItem[],
): Map<string, SessionWorkItem[]> {
  const map = new Map<string, SessionWorkItem[]>();
  for (const item of workItems) {
    const existing = map.get(item.session_id);
    if (existing) existing.push(item);
    else map.set(item.session_id, [item]);
  }
  return map;
}

function catalogsToNames(catalogs: SessionListCatalogs | undefined): {
  teamNames: Map<string, string>;
  workspaceNames: Map<string, string>;
} {
  const teamNames = new Map((catalogs?.teams ?? []).map((team) => [team.id, team.name]));
  const workspaceNames = new Map(
    (catalogs?.workspaces ?? []).map((workspace) => [workspace.workspace_id, workspace.name]),
  );
  return { teamNames, workspaceNames };
}

function emptyGroup<S>(key: string, label: string, isConversations: boolean): SessionGroup<S> {
  return { key, label, subgroups: [], sessions: [], isConversations };
}

export function project<S extends GroupableSession>(
  sessions: readonly S[],
  workItems: readonly SessionWorkItem[],
  query: SessionListQuery,
  catalogs?: SessionListCatalogs,
): SessionListView<S> {
  const workItemsBySession = workItemsMap(workItems);
  const { teamNames, workspaceNames } = catalogsToNames(catalogs);
  const ordered = sortSessions(sessions, query.sort);
  const groups = new Map<string, SessionGroup<S>>();
  const conversations: S[] = [];

  for (const session of ordered) {
    const item = primaryWorkItem(session, workItemsBySession);
    if (!item) {
      if (query.grouping !== 'workspace') {
        conversations.push(session);
        continue;
      }
      const level = workspaceLevel(session, workspaceNames);
      let group = groups.get(level.key);
      if (!group) {
        group = emptyGroup(level.key, level.label, false);
        groups.set(level.key, group);
      }
      group.sessions.push(session);
      continue;
    }
    if (query.roleFilter !== ALL_FILTER && session.persona_name !== query.roleFilter) continue;
    if (query.goalFilter !== ALL_FILTER && item.goal_id !== query.goalFilter) continue;
    if (
      query.workspaceFilter !== ALL_FILTER &&
      (session.workspace_id ?? null) !== query.workspaceFilter
    ) {
      continue;
    }
    if (query.statusFilter === 'active' && !isSessionRunning(workItemsBySession.get(session.id))) {
      continue;
    }

    const level = levelsFor(session, item, query.grouping, teamNames, workspaceNames);
    let group = groups.get(level.key);
    if (!group) {
      group = emptyGroup(level.key, level.label, false);
      groups.set(level.key, group);
    }
    if (!level.subKey) {
      group.sessions.push(session);
      continue;
    }
    let subgroup = group.subgroups.find((candidate) => candidate.key === level.subKey);
    if (!subgroup) {
      subgroup = { key: level.subKey, label: level.subLabel, sessions: [] };
      group.subgroups.push(subgroup);
    }
    subgroup.sessions.push(session);
  }

  const result = Array.from(groups.values());
  if (query.showEmptyGroups && catalogs) {
    appendEmptyCatalogGroups(result, query.grouping, catalogs, groups);
    if (!result.some((group) => group.isConversations) && conversations.length === 0) {
      result.push(emptyGroup(CONVERSATIONS_GROUP_KEY, CONVERSATIONS_GROUP_LABEL, true));
    }
  }
  if (conversations.length > 0) {
    result.push({
      key: CONVERSATIONS_GROUP_KEY,
      label: CONVERSATIONS_GROUP_LABEL,
      subgroups: [],
      sessions: conversations,
      isConversations: true,
    });
  }

  return {
    groups: result,
    roleOptions: sessionRoleOptions(sessions, workItemsBySession),
    goalOptions: sessionGoalOptions(sessions, workItemsBySession),
  };
}

function appendEmptyCatalogGroups<S>(
  result: SessionGroup<S>[],
  grouping: SessionGrouping,
  catalogs: SessionListCatalogs,
  existing: ReadonlyMap<string, SessionGroup<S>>,
): void {
  const add = (key: string, label: string) => {
    if (existing.has(key) || result.some((group) => group.key === key)) return;
    result.push(emptyGroup(key, label, false));
  };
  switch (grouping) {
    case 'goal':
      for (const goal of catalogs.goals) {
        add(goal.id, goal.objective);
      }
      break;
    case 'team':
      for (const team of catalogs.teams) {
        add(team.id, team.name);
      }
      break;
    case 'workspace':
      for (const workspace of catalogs.workspaces) {
        add(workspace.workspace_id, workspace.name);
      }
      break;
    case 'user-initiated':
      add(WORK_GROUP_KEY, WORK_GROUP_LABEL);
      break;
  }
}

export function flatten<S extends GroupableSession>(
  view: SessionListView<S>,
  collapsedKeys: ReadonlySet<string>,
): SessionListFlatRow<S>[] {
  const rows: SessionListFlatRow<S>[] = [];
  for (const group of view.groups) {
    rows.push({
      type: 'group',
      key: group.key,
      label: group.label,
      loadedCount: countGroupSessions(group),
    });
    if (collapsedKeys.has(group.key)) continue;
    for (const session of group.sessions) {
      rows.push({ type: 'session', session, groupKey: group.key, depth: 1 });
    }
    for (const subgroup of group.subgroups) {
      rows.push({
        type: 'subgroup',
        key: subgroup.key,
        label: subgroup.label,
        loadedCount: subgroup.sessions.length,
      });
      if (collapsedKeys.has(subgroup.key)) continue;
      for (const session of subgroup.sessions) {
        rows.push({
          type: 'session',
          session,
          groupKey: group.key,
          depth: 2,
        });
      }
    }
  }
  return rows;
}

function sessionRef<S extends GroupableSession>(session: S): SessionRef {
  return { session_id: session.id, workspace_id: session.workspace_id ?? null };
}

export function selectionInGroup<S extends GroupableSession>(
  view: SessionListView<S>,
  groupKey: string,
): SessionRef[] {
  const group = view.groups.find((candidate) => candidate.key === groupKey);
  if (!group) return [];
  const refs = group.sessions.map(sessionRef);
  for (const subgroup of group.subgroups) {
    refs.push(...subgroup.sessions.map(sessionRef));
  }
  return refs;
}
